import { useState } from "react";
import { Check, Copy, Loader2, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import type { CallbackRequestErrorCode } from "@/hooks/voice/useChannelRequests";
import { trackCallClick } from "@/lib/ga4";
import { isTelCapable, stripTel } from "@/lib/tel";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

// Simple E.164 check after stripping whitespace — a leading "+", a non-zero
// first digit, and 8–15 digits total. Good enough client-side gate; the
// backend owns real validation.
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

const ERROR_COPY: Record<CallbackRequestErrorCode, string> = {
  no_outbound_binding: "This Genie can't place outbound calls yet — try the number above instead.",
  rate_limited: "Too many callback requests right now. Please try again in a few minutes.",
  outbound_failed: "We couldn't start that call. Please try again, or call the number above.",
  unknown: "Something went wrong. Please try again.",
};

export interface CallStageRequestResult {
  ok: boolean;
  errorCode?: CallbackRequestErrorCode;
  errorMessage?: string;
}

interface CallStageProps {
  brandColor: string;
  secondaryColor?: string | null;
  agentName?: string | null;
  /** The tenant's own contact number, from `agent.phone.number`. */
  phoneNumber?: string | null;
  /** The Genie is bound to Outbound (`agent.outbound_connected`), so the callback form can work. */
  callbackAvailable: boolean;
  onRequestCallback: (args: { phoneE164: string; visitorName?: string; pageUrl: string }) => Promise<CallStageRequestResult>;
  pageUrl: string;
}

/**
 * The "Call" channel stage — fills the switcher's stage in place of the orb.
 * Two independent blocks: dial the Genie's own number, or leave a number for
 * a one-time callback. Either can be absent; the caller hides the whole tab
 * when both are.
 */
export const CallStage = ({
  brandColor,
  secondaryColor,
  agentName,
  phoneNumber,
  callbackAvailable,
  onRequestCallback,
  pageUrl,
}: CallStageProps) => {
  const end = secondaryColor || "#FF4DB8";
  const gradient = `linear-gradient(90deg, ${brandColor} 0%, ${end} 100%)`;
  const telCapable = isTelCapable();

  const [phoneValue, setPhoneValue] = useState("");
  const [nameValue, setNameValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CallStageRequestResult | null>(null);
  const [copied, setCopied] = useState(false);

  const showDialBlock = !!phoneNumber;
  const showCallbackBlock = callbackAvailable;

  const handleCopyNumber = async () => {
    if (!phoneNumber) return;
    try {
      await navigator.clipboard.writeText(phoneNumber);
      setCopied(true);
      toast.success("Phone number copied");
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — try again");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const stripped = phoneValue.replace(/\s+/g, "");
    if (!E164_PATTERN.test(stripped)) {
      setValidationError("Enter your number with a country code, e.g. +14155550100.");
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    setResult(null);
    try {
      const res = await onRequestCallback({
        phoneE164: stripped,
        visitorName: nameValue.trim() || undefined,
        pageUrl,
      });
      setResult(res);
    } finally {
      setSubmitting(false);
    }
  };

  if (!showDialBlock && !showCallbackBlock) {
    // Neither block applies (no provisioned number, no live callback binding)
    // yet the tab is showing — the caller only does this on localhost via
    // `?debugChannels=1`. A calm state beats an empty stage.
    return (
      <div className="flex w-full max-w-[420px] flex-col items-center gap-3 rounded-3xl border border-[#E9EDF3] bg-white p-6 text-center shadow-[0_10px_30px_-24px_rgba(15,23,42,0.4)]">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-2xl"
          style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
        >
          <GenieIcon name="phone" size={22} color={brandColor} secondaryColor={end} gradientEnabled />
        </span>
        <p className="text-[14px] font-medium text-[#64748B]">{`${agentName || "This Genie"} isn't set up to call yet.`}</p>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-[420px] flex-col gap-5">
      {showDialBlock && (
        <div className="rounded-3xl border border-[#E9EDF3] bg-white p-5 text-center shadow-[0_10px_30px_-24px_rgba(15,23,42,0.4)]">
          <span
            className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl"
            style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
          >
            <GenieIcon name="phone" size={22} color={brandColor} secondaryColor={end} gradientEnabled />
          </span>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-[#9AA7B6]">Call this number</p>
          <a
            href={`tel:${stripTel(phoneNumber!)}`}
            onClick={() => trackCallClick("call_stage")}
            className="mt-1.5 block text-[24px] font-semibold leading-tight text-[#222B45] transition-opacity hover:opacity-80"
          >
            {phoneNumber}
          </a>
          <p className="mt-1 text-[13px] text-[#64748B]">
            Reaches {agentName || "this Genie"} directly.
          </p>
          {!telCapable && (
            <button
              type="button"
              onClick={() => void handleCopyNumber()}
              className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: gradient }}
            >
              {copied ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2.25} />}
              {copied ? "Copied" : "Copy number"}
            </button>
          )}
        </div>
      )}

      {showCallbackBlock && (
        <div className="rounded-3xl border border-[#E9EDF3] bg-white p-5 shadow-[0_10px_30px_-24px_rgba(15,23,42,0.4)]">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-[#9AA7B6]">Or we'll call you</p>

          {result?.ok ? (
            <div className="mt-3 flex items-center gap-3">
              <div
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: gradient, boxShadow: `0 10px 24px -10px ${brandColor}80` }}
              >
                <Check size={20} strokeWidth={2.75} />
              </div>
              <div className="min-w-0">
                <h3 className="text-[15px] font-semibold leading-tight text-[#222B45]">Ringing you now</h3>
                <p className="mt-0.5 text-[13px] leading-snug text-[#64748B]">
                  Keep your phone handy — {agentName || "your Genie"} is calling.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)} className="mt-3 flex flex-col gap-3">
              <div>
                <label htmlFor="callback-phone" className="sr-only">
                  Your phone number
                </label>
                <input
                  id="callback-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={phoneValue}
                  onChange={(e) => {
                    setPhoneValue(e.target.value);
                    if (validationError) setValidationError(null);
                  }}
                  placeholder="+1 415 555 0100"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-genie-primary transition-shadow"
                />
              </div>
              <div>
                <label htmlFor="callback-name" className="sr-only">
                  Your name
                </label>
                <input
                  id="callback-name"
                  type="text"
                  autoComplete="name"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-genie-primary transition-shadow"
                />
              </div>

              {validationError && <p className="text-[13px] font-medium text-red-600">{validationError}</p>}
              {result && !result.ok && (
                <p className="flex items-start gap-1.5 text-[13px] font-medium text-red-600">
                  <PhoneOff size={14} strokeWidth={2.25} className="mt-0.5 flex-shrink-0" />
                  {ERROR_COPY[result.errorCode || "unknown"]}
                </p>
              )}

              <p className="text-[12px] leading-snug text-[#94A3B8]">
                We'll call you once from this Genie. Standard call rates may apply.
              </p>

              <button
                type="submit"
                disabled={submitting}
                className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-semibold text-white shadow-lg transition-all hover:scale-[1.02] hover:opacity-95 disabled:cursor-wait disabled:opacity-70 disabled:hover:scale-100"
                style={{ background: gradient, boxShadow: `0 10px 28px -10px ${brandColor}80` }}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Calling…
                  </>
                ) : (
                  "Call me back"
                )}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default CallStage;
